import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_EXTERNAL_URL,
  EMPTY_ARTICLE_FORM,
  NEWS_CATEGORY_OPTIONS,
  NEWS_SOURCE_OPTIONS,
  NEWS_SOURCES,
  API_URL,
  resolveAdminImagePreviewUrl,
} from './newsConfig';
import { uploadNewsImage } from './adminAuth';

const inputClassName =
  'w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cpre-new-yew/30 focus:border-cpre-new-yew';
const labelClassName = 'block text-sm font-medium text-cpre-new-yew mb-1';

export default function NewsArticleForm({
  formMode,
  formData,
  formError,
  formSubmitting,
  onChange,
  onFormDataChange,
  onSubmit,
  onCancel,
}) {
  const navigate = useNavigate();
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  const isNational = formData.source === NEWS_SOURCES.NATIONAL;

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImageUploadError(null);
    setImageUploading(true);
    try {
      const imageUrl = await uploadNewsImage(API_URL, file, navigate);
      onFormDataChange({ ...formData, image_url: imageUrl });
    } catch (err) {
      setImageUploadError(err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const handleSourceChange = (source) => {
    onFormDataChange({
      ...formData,
      source,
      external_url:
        source === NEWS_SOURCES.NATIONAL
          ? formData.external_url || DEFAULT_EXTERNAL_URL
          : '',
    });
  };

  return (
    <div className="bg-white shadow-sm border border-stone-200 rounded-2xl p-6 mb-8">
      <h2 className="text-xl font-heading font-semibold text-cpre-new-yew mb-2">
        {formMode === 'create' ? 'Create New Article' : 'Edit Article'}
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Choose Local or National news first. Local articles open on this website; National articles
        link out via an external URL.
      </p>

      {formError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 whitespace-pre-line">
          {formError}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="mb-6">
          <span className={labelClassName}>News type *</span>
          <div className="flex flex-wrap gap-3">
            {NEWS_SOURCE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSourceChange(option.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  formData.source === option.id
                    ? 'bg-cpre-new-yew text-white'
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClassName} htmlFor="title">
              Title *
            </label>
            <input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={onChange}
              required
              className={inputClassName}
            />
          </div>
          <div>
            <label className={labelClassName} htmlFor="category">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={onChange}
              required
              className={inputClassName}
            >
              {NEWS_CATEGORY_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClassName} htmlFor="excerpt">
            Excerpt *
          </label>
          <input
            id="excerpt"
            type="text"
            name="excerpt"
            value={formData.excerpt}
            onChange={onChange}
            required
            className={inputClassName}
          />
        </div>

        <div className="mb-4">
          <label className={labelClassName} htmlFor="content">
            Content *
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={onChange}
            required
            rows={8}
            className={inputClassName}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Supports **bold**, plain URLs, and [label](mailto:email@example.com) links.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClassName} htmlFor="author">
              Author
            </label>
            <input
              id="author"
              type="text"
              name="author"
              value={formData.author}
              onChange={onChange}
              className={inputClassName}
            />
          </div>
          <div>
            <label className={labelClassName} htmlFor="created_at">
              Publication date
            </label>
            <input
              id="created_at"
              type="date"
              name="created_at"
              value={formData.created_at}
              onChange={onChange}
              className={inputClassName}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClassName} htmlFor="image_url">
            Image URL
          </label>
          <input
            id="image_url"
            type="text"
            name="image_url"
            value={formData.image_url}
            onChange={onChange}
            placeholder="/images/cpre images for website/38.jpg"
            className={inputClassName}
          />
          <div className="mt-3">
            <label className={labelClassName} htmlFor="image_file">
              Or upload from your device
            </label>
            <input
              id="image_file"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleImageFileChange}
              disabled={imageUploading || formSubmitting}
              className="block w-full text-sm text-stone-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-cpre-new-yew file:text-white hover:file:bg-cpre-hill disabled:opacity-60"
            />
            <p className="text-xs text-muted-foreground mt-2">
              JPEG, PNG, GIF, or WebP. Max 10 MB. Upload fills the Image URL field above.
            </p>
            {imageUploading && (
              <p className="text-xs text-cpre-new-yew mt-1">Uploading image…</p>
            )}
            {imageUploadError && (
              <p className="text-xs text-red-600 mt-1">{imageUploadError}</p>
            )}
          </div>
          {formData.image_url && (
            <img
              src={resolveAdminImagePreviewUrl(formData.image_url)}
              alt="Article preview"
              className="mt-3 max-h-40 rounded-lg border border-stone-200 object-cover"
            />
          )}
        </div>

        {isNational && (
          <div className="mb-4">
            <label className={labelClassName} htmlFor="external_url">
              External link *
            </label>
            <input
              id="external_url"
              type="text"
              name="external_url"
              value={formData.external_url}
              onChange={onChange}
              required
              placeholder={DEFAULT_EXTERNAL_URL}
              className={inputClassName}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Use https://www.cpre.org.uk/news/, a petition URL, or mailto:email@example.com for
              special cases.
            </p>
          </div>
        )}

        <div className="flex items-center mb-6">
          <input
            id="published"
            type="checkbox"
            name="published"
            checked={formData.published}
            onChange={onChange}
            className="h-4 w-4 text-cpre-new-yew focus:ring-cpre-new-yew border-stone-300 rounded"
          />
          <label htmlFor="published" className="ml-2 block text-sm text-stone-700">
            Published (visible on the public News page)
          </label>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            type="submit"
            disabled={formSubmitting}
            className={`px-6 py-2 rounded-full text-white font-medium ${
              formSubmitting
                ? 'bg-cpre-new-yew/60 cursor-not-allowed'
                : 'bg-cpre-new-yew hover:bg-cpre-hill'
            }`}
          >
            {formSubmitting
              ? 'Saving...'
              : formMode === 'create'
              ? 'Create Article'
              : 'Update Article'}
          </button>
          {formMode === 'edit' && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 bg-stone-100 text-stone-700 rounded-full hover:bg-stone-200"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
